import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { AlertTriangle, CheckCircle2, ImagePlus, Loader2, Trash2, UserRound } from 'lucide-react'
import * as api from '../api/auth'
import { ApiError } from '../api/auth'
import { Avatar } from '../components/Avatar'
import { useAuth } from '../context/AuthContext'
import { compressAvatar } from '../lib/image'
import type { AccountProfile } from '../types/identity'

const INPUT =
  'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base sm:text-sm dark:border-white/10 dark:bg-white/5'
const BUTTON =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-salsa-500 px-4 py-2 text-sm font-semibold text-white hover:bg-salsa-600 disabled:opacity-60'
const SUBTLE =
  'inline-flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5'
const CARD = 'card space-y-3 p-4'
const LABEL =
  'text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500'
const HINT = 'block text-[11px] text-slate-500 dark:text-slate-400'
const BANNER = 'flex items-start gap-2 rounded-2xl border px-4 py-3 text-xs'
const ERROR_TONE =
  'border-salsa-200 bg-salsa-50/70 text-salsa-800 dark:border-salsa-500/25 dark:bg-salsa-500/10 dark:text-salsa-200'
const OK_TONE =
  'border-emerald-200 bg-emerald-50/70 text-emerald-800 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-200'

/**
 * Profile Settings (D-44): your full legal name, your nickname, your picture.
 *
 * Nothing here is a sensitive change, so no password or e-mailed code is asked for — the
 * screen stays a two-card, one-click affair. The name you type as your legal name is the
 * formal record; the nickname is only what the app calls you.
 */
export function ProfileSettingsPage() {
  const { refresh } = useAuth()
  const [profile, setProfile] = useState<AccountProfile | null>(null)
  const [fullName, setFullName] = useState('')
  const [nickname, setNickname] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<'details' | 'picture' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await api.fetchAccountProfile()
      setProfile(data)
      setFullName(data.full_name)
      setNickname(data.nickname ?? '')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load your profile')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function run(
    which: 'details' | 'picture',
    action: () => Promise<AccountProfile>,
    message: string,
  ) {
    setBusy(which)
    setError(null)
    setNotice(null)
    try {
      const saved = await action()
      setProfile(saved)
      setFullName(saved.full_name)
      setNickname(saved.nickname ?? '')
      setNotice(message)
      // The top bar shows the nickname and the picture, so the session is re-read.
      await refresh()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save your profile')
    } finally {
      setBusy(null)
    }
  }

  const saveDetails = (event: FormEvent) => {
    event.preventDefault()
    void run(
      'details',
      () => api.updateAccountProfile({ full_name: fullName, nickname }),
      'Your details are saved.',
    )
  }

  async function pickPicture(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = '' // so picking the same file twice still fires
    if (!file) return
    setError(null)
    setNotice(null)
    try {
      const dataUrl = await compressAvatar(file)
      await run('picture', () => api.updateAccountProfile({ avatar: dataUrl }), 'Your picture is saved.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That picture could not be used')
    }
  }

  const removePicture = () =>
    void run('picture', () => api.updateAccountProfile({ avatar: null }), 'Your picture is removed.')

  if (loading && !profile) {
    return (
      <p className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Loading your profile…
      </p>
    )
  }

  return (
    <div className="space-y-5">
      {error && (
        <div className={`${BANNER} ${ERROR_TONE}`}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div className={`${BANNER} ${OK_TONE}`}>
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{notice}</span>
        </div>
      )}

      <section className={CARD}>
        <div className="flex items-center gap-2">
          <UserRound className="h-4 w-4 text-salsa-500" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Your details</h2>
        </div>

        <form onSubmit={saveDetails} className="space-y-3">
          <div className="grid gap-3 lg:grid-cols-2">
            <label className="space-y-1">
              <span className={LABEL}>Full legal name</span>
              <input
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                required
                minLength={2}
                maxLength={200}
                className={INPUT}
                placeholder="Your full name"
              />
              <span className={HINT}>
                Your official name. The account list, the audit log and every e-mail use this one.
              </span>
            </label>

            <label className="space-y-1">
              <span className={LABEL}>Nickname</span>
              <input
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
                maxLength={40}
                className={INPUT}
                placeholder="Optional"
              />
              <span className={HINT}>
                What the app calls you, in the top bar and in greetings. Leave it empty to use
                your legal name.
              </span>
            </label>
          </div>

          <button type="submit" disabled={busy !== null} className={BUTTON}>
            {busy === 'details' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            )}
            Save details
          </button>
        </form>
      </section>

      <section className={CARD}>
        <div className="flex items-center gap-2">
          <ImagePlus className="h-4 w-4 text-salsa-500" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Profile picture</h2>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <Avatar
            name={profile?.full_name ?? 'You'}
            avatar={profile?.avatar}
            size="lg"
          />

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <label className={`${SUBTLE} cursor-pointer`}>
                <ImagePlus className="h-3.5 w-3.5" aria-hidden="true" />
                {profile?.avatar ? 'Replace picture' : 'Choose a picture'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={pickPicture}
                  className="hidden"
                  disabled={busy !== null}
                />
              </label>

              {profile?.avatar && (
                <button
                  type="button"
                  onClick={removePicture}
                  disabled={busy !== null}
                  className={SUBTLE}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Remove picture
                </button>
              )}

              {busy === 'picture' && (
                <span className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> Saving…
                </span>
              )}
            </div>

            <p className={HINT}>
              PNG, JPEG or WebP. It is square-cropped and shrunk to 256×256 in your browser
              before it is saved, so no original photo ever leaves your machine.
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}